interface GraphQLErrorLike {
  message?: unknown;
  extensions?: { details?: unknown };
}

interface ApolloErrorLike {
  graphQLErrors: GraphQLErrorLike[];
  networkError?: unknown;
}

interface ReportHint {
  originalException?: unknown;
  captureContext?: unknown;
}

const isObject = (value: unknown): value is Record<string, unknown> => value != null && typeof value === 'object';

const UNHANDLED_EXCEPTION_PATTERN =
  /(?:[A-Z][A-Za-z0-9]*::)+[A-Z][A-Za-z0-9]*|\b[A-Z][A-Za-z0-9]*(?:Error|Exception|Violation)\b|\bERROR:\s/;

const looksLikeUnhandledException = (message: unknown): boolean =>
  typeof message === 'string' && UNHANDLED_EXCEPTION_PATTERN.test(message);

const isSymbolicRefusal = (value: unknown): boolean => {
  if (!isObject(value) || !isObject(value.extensions)) return false;
  if (looksLikeUnhandledException(value.message)) return false;
  const { details } = value.extensions;
  return isObject(details) && Object.keys(details).length > 0;
};

const isApolloErrorLike = (value: unknown): value is ApolloErrorLike =>
  isObject(value) && Array.isArray(value.graphQLErrors);

const areAllRefusals = (errors: unknown[]): boolean => errors.length > 0 && errors.every(isSymbolicRefusal);

const upstreamError = (value: Record<string, unknown>): Record<string, unknown> => {
  if (isObject(value.previousError)) return value.previousError;
  if (isObject(value.props) && isObject(value.props.previousError)) return value.props.previousError;
  return value;
};

const responseErrors = (value: unknown): unknown[] | undefined => {
  if (!isObject(value)) return undefined;
  const { response } = upstreamError(value);
  if (!isObject(response)) return undefined;
  if (Array.isArray(response.data)) return response.data;
  if (isObject(response.data) && Array.isArray(response.data.errors)) return response.data.errors;
  return undefined;
};

const isRefusalError = (value: unknown): boolean => {
  if (Array.isArray(value)) return areAllRefusals(value);
  if (isApolloErrorLike(value)) return value.networkError == null && areAllRefusals(value.graphQLErrors);
  const httpErrors = responseErrors(value);
  if (httpErrors != null) return areAllRefusals(httpErrors);
  return isSymbolicRefusal(value);
};

const contextValues = (captureContext: unknown, key: 'extra' | 'contexts'): unknown[] => {
  if (!isObject(captureContext) || !isObject(captureContext[key])) return [];
  return Object.values(captureContext[key]);
};

export const isExpectedRefusalReport = (hint: ReportHint | undefined): boolean => {
  if (hint == null) return false;
  return [
    hint.originalException,
    ...contextValues(hint.captureContext, 'extra'),
    ...contextValues(hint.captureContext, 'contexts'),
  ].some(isRefusalError);
};
